import { useEffect, type ChangeEvent } from "react";

type ScheduleModalProps = {
    open: boolean;
    title: string;
    description: string;
    startsAt: string;
    endsAt: string;
    onStartsAtChange: (value: string) => void;
    onEndsAtChange: (value: string) => void;
    onClose: () => void;
    onSubmit: () => void;
    isPending: boolean;
    pendingLabel: string;
    submitLabel: string;
};





export default function ScheduleModal({
    open,
    title,
    description,
    startsAt,
    endsAt,
    onStartsAtChange,
    onEndsAtChange,
    onClose,
    onSubmit,
    isPending,
    pendingLabel,
    submitLabel,
}: ScheduleModalProps) {

    useEffect(() => {
        if (!open) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, onClose]);



    if (!open) return null;


    return (
        <div
            className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/40 px-4"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
                <div className="flex items-center justify-between gap-4">
                    <h2
                        className="
                            font-heading
                            text-lg
                            font-normal    
                            bg-[#f5efe7]
                            px-[0.775rem]
                            py-[0.2rem]
                            rounded-[4px]
                        "
                    >
                        {title}
                    </h2>

                    <button
                        type="button"
                        onClick={onClose}
                        className="text-foreground-400 hover:text-foreground-700 cursor-pointer"
                        aria-label="Fermer"
                    >
                        <i className="ri-close-line text-xl"></i>
                    </button>
                </div>

                <p className="mt-2 text-sm text-foreground-500">
                    {description}
                </p>

                <div className="mt-5 space-y-4">
                    <div>
                        <label
                            htmlFor="schedule-starts-at"
                            className="block text-sm font-medium text-foreground-700"
                        >
                            Date et heure de début
                        </label>

                        <input
                            id="schedule-starts-at"
                            type="datetime-local"
                            value={startsAt}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                onStartsAtChange(e.target.value)
                            }
                            className="mt-1.5 w-full rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="schedule-ends-at"
                            className="block text-sm font-medium text-foreground-700"
                        >
                            Date et heure de fin
                        </label>

                        <input
                            id="schedule-ends-at"
                            type="datetime-local"
                            min={startsAt || undefined}
                            value={endsAt}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                onEndsAtChange(e.target.value)
                            }
                            className="mt-1.5 w-full rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-md border border-background-300 text-foreground-700 text-sm font-semibold hover:bg-background-50 transition-colors cursor-pointer"
                    >
                        Annuler
                    </button>

                    <button
                        type="button"
                        onClick={onSubmit}
                        disabled={!startsAt || isPending}
                        className="px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isPending ? pendingLabel : submitLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}